import { useEffect, useId, useRef, useState } from 'react';

// T139 — shared "?" help affordance + popover. A small round "?" button that
// opens a role="tooltip" panel explaining a dense piece of UI. Hover, keyboard
// focus, and click/tap all OPEN it (click opens rather than toggles: on touch
// there is no hover and focus fires before click, so a toggle would cancel
// itself and the tap would never open); outside-click (mousedown) and Escape
// close. Keyboard- + screen-reader-first (principle 11): the trigger is a real
// <button> with an aria-label (the "?" glyph itself is aria-hidden), and the
// panel is a role="tooltip" linked via aria-describedby (the canonical tooltip
// pattern — no aria-expanded, which is for disclosure/menu controls).
//
// Theme-safe: the panel uses bg-slate-900/90 (retargeted in index.css to
// --c-surface-1), not the un-retargeted /95 the older popovers used, so it
// actually retints per [data-theme]. z-30 keeps it BELOW the JobDrawer scrim
// (z-40), matching the inline-overlay (no-portal) convention.
const SIDE = { right: 'right-0', left: 'left-0' };

export default function HelpPopover({
  label = 'Help',   // SR name for the "?" button
  title,            // bold heading inside the panel
  children,         // panel body
  width = 'w-72',
  side = 'right',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Close ONLY the popover — stop the keydown reaching a parent overlay's
        // own Escape handler (e.g. JobDrawer closes on a window-level Escape;
        // without this, one press would close both the popover AND the drawer).
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        aria-label={label}
        aria-describedby={open ? panelId : undefined}
        className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-slate-700 align-middle text-[10px] font-medium leading-none text-slate-400 transition hover:border-brand-600 hover:text-brand-400"
      >
        <span aria-hidden="true">?</span>
      </button>
      {open && (
        <div
          id={panelId}
          role="tooltip"
          className={
            `absolute ${SIDE[side]} top-full z-30 mt-2 ${width} rounded-lg border ` +
            'border-slate-700 bg-slate-900/90 p-3 text-left text-xs normal-case tracking-normal text-slate-200 shadow-xl backdrop-blur'
          }
        >
          {title && <p className="font-semibold text-slate-100">{title}</p>}
          <div className={(title ? 'mt-1 ' : '') + 'leading-snug text-slate-300'}>{children}</div>
        </div>
      )}
    </span>
  );
}
